import React from 'react'
import DatePicker from 'react-datepicker'

import { toDateKey, fromDateKey } from '../../utils/date-keys'
import { MEAL_OPTIONS } from '../../../constants/inquiry-options'

const formatDateDisplay = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

const getDaysInRange = (start, end) => {
    const days = []
    const current = new Date(start)
    current.setHours(0, 0, 0, 0)
    const endDay = new Date(end)
    endDay.setHours(0, 0, 0, 0)
    while (current <= endDay) {
        days.push(new Date(current))
        current.setDate(current.getDate() + 1)
    }
    return days
}

// Dates live in the parent form's fields (as YYYY-MM-DD keys) rather than local
// state, so they survive the section collapsing and re-opening via Edit.
const DateRangeSchedule = ({ startKey, endKey, schedule, onScheduleChange, onDatesChange, hasError }) => {
    const startDate = fromDateKey(startKey)
    const endDate = fromDateKey(endKey)
    const days = startDate && endDate ? getDaysInRange(startDate, endDate) : []

    const rebuildDays = (start, end) => {
        if (start && end) {
            const validKeys = new Set(getDaysInRange(start, end).map(d => toDateKey(d)))
            const trimmed = (schedule || []).filter(s => validKeys.has(s.date))
            onScheduleChange(trimmed)
            onDatesChange(toDateKey(start), toDateKey(end))
        } else {
            onScheduleChange([])
            onDatesChange(toDateKey(start), '')
        }
    }

    const handleStartChange = (date) => {
        const keepEnd = endDate && date && endDate >= date ? endDate : null
        rebuildDays(date, keepEnd)
    }

    const handleEndChange = (date) => {
        rebuildDays(startDate, date)
    }

    const getMealsForDay = (day) => {
        const dateStr = toDateKey(day)
        const entry = (schedule || []).find(s => s.date === dateStr)
        return entry ? entry.meals : []
    }

    const toggleMeal = (day, mealValue) => {
        const dateStr = toDateKey(day)
        const existing = (schedule || []).find(s => s.date === dateStr)
        let newSchedule
        if (!existing) {
            newSchedule = [...(schedule || []), { date: dateStr, meals: [mealValue] }]
        } else {
            const updatedMeals = existing.meals.includes(mealValue)
                ? existing.meals.filter(m => m !== mealValue)
                : [...existing.meals, mealValue]
            if (updatedMeals.length === 0) {
                newSchedule = (schedule || []).filter(s => s.date !== dateStr)
            } else {
                newSchedule = (schedule || []).map(s => s.date === dateStr ? { date: dateStr, meals: updatedMeals } : s)
            }
        }
        onScheduleChange(newSchedule)
    }

    return (
        <div className="input-wrapper flex-100">
            <div className="form-group">
                <div className={`input-wrapper flex-50 ${hasError && !startDate ? 'has-error' : ''}`}>
                    <label className="form-label">Start Date*</label>
                    <DatePicker
                        selected={startDate}
                        onChange={handleStartChange}
                        minDate={new Date()}
                        placeholderText="Select start date"
                        className="form-control"
                        dateFormat="MMMM d, yyyy"
                        showPopperArrow={false} />
                </div>
                <div className={`input-wrapper flex-50 ${hasError && !endDate ? 'has-error' : ''}`}>
                    <label className="form-label">End Date*</label>
                    <DatePicker
                        selected={endDate}
                        onChange={handleEndChange}
                        minDate={startDate || new Date()}
                        placeholderText={startDate ? 'Select end date' : 'Select a start date first'}
                        className="form-control"
                        dateFormat="MMMM d, yyyy"
                        showPopperArrow={false}
                        disabled={!startDate} />
                </div>
            </div>
            {days.length > 0 && (
                <div className="date-range-schedule__meals">
                    <label className="form-label">Which meals would you like each day?*</label>
                    {days.map((day) => (
                        <div key={toDateKey(day)} className="meal-day-row">
                            <span className="meal-day-row__date">{formatDateDisplay(day)}</span>
                            <div className="option-btns">
                                {MEAL_OPTIONS.map((meal) => (
                                    <span
                                        key={meal.value}
                                        className={`btn btn-select ${getMealsForDay(day).includes(meal.value) ? 'active' : ''}`}
                                        onClick={() => toggleMeal(day, meal.value)}>
                                        {meal.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default DateRangeSchedule
